import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Switch,
  Table,
  Typography,
} from '@mui/joy';
import type { SxProps } from '@mui/joy/styles/types';
import type { CatalogueStatus, StatusFilter } from '../types';
import { resolveCatalogueAssetUrl } from '../data/assets';
import { statusLabel } from '../data/status';

export function StatusChip({ status }: { status: CatalogueStatus }) {
  const color = status === 'active' ? 'success' : status === 'divested' ? 'warning' : 'neutral';
  return (
    <Chip size="sm" color={color} variant="soft">
      {statusLabel(status)}
    </Chip>
  );
}

export function StatusFilterSelect({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (value: StatusFilter) => void;
}) {
  return (
    <FormControl sx={{ minWidth: 180 }}>
      <FormLabel>Status</FormLabel>
      <Select
        value={value}
        onChange={(_, next) => onChange((next as StatusFilter) || 'active')}
      >
        <Option value="active">Active (hide deprecated)</Option>
        <Option value="divested">Divested</Option>
        <Option value="deprecated">Deprecated</Option>
        <Option value="all">All</Option>
      </Select>
    </FormControl>
  );
}

export function AssetPathField({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const preview = resolveCatalogueAssetUrl(value);
  const [broken, setBroken] = useState(false);

  return (
    <Box>
      <FormControl>
        <FormLabel>Asset path</FormLabel>
        <Input
          value={value}
          placeholder="/door-slabs/shoreham.svg"
          onChange={(event) => {
            setBroken(false);
            onChange(event.target.value);
          }}
        />
      </FormControl>
      {preview && !broken ? (
        <Box
          sx={{
            mt: 1,
            width: 96,
            height: 96,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 'sm',
            overflow: 'hidden',
            bgcolor: 'neutral.softBg',
          }}
        >
          <img
            src={preview}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={() => setBroken(true)}
          />
        </Box>
      ) : preview && broken ? (
        <Typography level="body-xs" sx={{ mt: 0.5 }} color="neutral">
          No preview for {preview}
        </Typography>
      ) : null}
    </Box>
  );
}

export function DivestFields({
  divest,
  deprecatedDate,
  onDivest,
  onDeprecatedDate,
}: {
  divest: boolean;
  deprecatedDate: string;
  onDivest: (value: boolean) => void;
  onDeprecatedDate: (value: string) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <FormControl>
        <FormLabel>Divest</FormLabel>
        <Switch checked={divest} onChange={(event) => onDivest(event.target.checked)} />
      </FormControl>
      <FormControl>
        <FormLabel>Deprecated date</FormLabel>
        <Input
          type="date"
          value={deprecatedDate}
          onChange={(event) => onDeprecatedDate(event.target.value)}
        />
      </FormControl>
    </Box>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Delete',
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog>
        <Typography level="title-lg">{title}</Typography>
        <Typography sx={{ my: 2 }}>{message}</Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button color="danger" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      {children}
    </FormControl>
  );
}

export function Toolbar({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end', mb: 2 }}>
      {children}
    </Box>
  );
}

export function OracleTable({ children }: { children: React.ReactNode }) {
  return (
    <Sheet variant="outlined" sx={{ overflow: 'auto', borderRadius: 'sm' }}>
      <Table
        stickyHeader
        size="sm"
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& thead th': { whiteSpace: 'nowrap' },
          '& tbody td': { verticalAlign: 'middle' },
        }}
      >
        {children}
      </Table>
    </Sheet>
  );
}

export type TableSortDirection = 'asc' | 'desc';

export function toggleTableSort<K extends string>(
  currentKey: K,
  currentDir: TableSortDirection,
  nextKey: K
): { key: K; direction: TableSortDirection } {
  if (currentKey === nextKey) {
    return { key: nextKey, direction: currentDir === 'asc' ? 'desc' : 'asc' };
  }
  return { key: nextKey, direction: 'asc' };
}

export function sortRowsByText<T>(
  rows: T[],
  getText: (row: T) => string,
  direction: TableSortDirection
): T[] {
  return [...rows].sort((a, b) => {
    const cmp = getText(a).trim().localeCompare(getText(b).trim(), undefined, { sensitivity: 'base' });
    return direction === 'asc' ? cmp : -cmp;
  });
}

export function SortableTableHeader({
  label,
  active,
  direction,
  onSort,
  style,
}: {
  label: string;
  active: boolean;
  direction: TableSortDirection;
  onSort: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <th
      style={style}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <Box
        component="button"
        type="button"
        onClick={onSort}
        title={active ? (direction === 'asc' ? 'Sorted A–Z' : 'Sorted Z–A') : 'Sort A–Z'}
        sx={{
          appearance: 'none',
          border: 'none',
          background: 'none',
          p: 0,
          m: 0,
          font: 'inherit',
          fontWeight: 'lg',
          color: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': { color: 'primary.500' },
        }}
      >
        {label}
        {active ? (
          <Typography component="span" level="body-xs" sx={{ color: 'neutral.500' }}>
            {direction === 'asc' ? '↑' : '↓'}
          </Typography>
        ) : null}
      </Box>
    </th>
  );
}

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  sx,
}: {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps;
}) {
  return (
    <Sheet variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 'sm', ...sx }}>
      {title || subtitle || action ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            mb: children ? 1.5 : 0,
          }}
        >
          <Box>
            {title ? <Typography level="title-md">{title}</Typography> : null}
            {subtitle ? (
              <Typography level="body-sm" sx={{ color: 'neutral.500', mt: title ? 0.25 : 0 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Box>
      ) : null}
      {children}
    </Sheet>
  );
}

export function FormGrid({
  columns = 2,
  children,
}: {
  columns?: 2 | 3 | 4;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        gap: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

export function ActionRow({
  children,
  sx,
}: {
  children: React.ReactNode;
  sx?: SxProps;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ...sx }}>
      {children}
    </Box>
  );
}

export function TruncatedText({
  value,
  maxWidth = 280,
}: {
  value: string | null | undefined;
  maxWidth?: number | string;
}) {
  const text = value?.trim() || '—';
  return (
    <Typography
      component="span"
      level="body-sm"
      title={value?.trim() || undefined}
      sx={{
        display: 'block',
        maxWidth,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Typography>
  );
}
